import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG } from '../config/api.config';
import { ReservationKycRecord, ReservationRecord, ReservationResult } from '../models/reservation.model';
import { SKIP_AUTH_REFRESH } from '../../interceptors/auth.interceptor';

export interface HoldExtensionRequest {
    id: string;
    reservationId: string;
    propertyId: string;
    projectId: string | null;
    requestedByUserId: string | null;
    requesterName: string | null;
    requestedAt: string | null;
    originalHoldExpiry: string | null;
    decisionDeadlineAt: string | null;
    extensionHours: number | null;
    approverUserIds: string[];
    status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'expired' | string;
    decidedByUserId: string | null;
    decisionComment: string | null;
    decidedAt: string | null;
    newHoldExpiry: string | null;
    propertyTitle: string | null;
    unitNumber: string | null;
    projectName: string | null;
    canDecide: boolean;
}

export interface HoldExtensionEvent {
    id: string;
    requestId: string;
    action: string;
    comment: string | null;
    actorName: string | null;
    createdAt: string | null;
}

export interface HoldExtensionHistory {
    requests: HoldExtensionRequest[];
    events: HoldExtensionEvent[];
}

@Injectable({
    providedIn: 'root'
})
export class ReservationsService {
    private http = inject(HttpClient);
    private readonly API2_PATH = `${API_CONFIG.API_BASE_URL}/API2/reservations`;
    private readonly API2_ROOT = `${API_CONFIG.API_BASE_URL}/API2`;

    getReservations(
        filter: { propertyId?: string; search?: string; signed?: boolean; status?: string } = {},
        options: { page?: number; perPage?: number } = {}
    ): Observable<ReservationRecord[]> {
        const page = options.page ?? 1;
        const perPage = options.perPage ?? 50;
        let params = new HttpParams().set('page', String(page)).set('per_page', String(perPage));
        if (filter.propertyId) params = params.set('property_id', filter.propertyId);
        if (filter.search) params = params.set('search', String(filter.search));
        if (filter.signed === true) params = params.set('signed', 'true');
        else if (filter.signed === false) params = params.set('signed', 'false');
        // Comma-separated lead statuses; the endpoint is shared with the leads
        // list, so reservations surfaces pass the reserved family explicitly.
        if (filter.status) params = params.set('status', String(filter.status));

        return this.http.get<any>(this.API2_PATH, { params }).pipe(
            map((response) => {
                const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response?.DATA) ? response.DATA : [];
                return rows.map((row: any) => this.mapApi2Reservation(row));
            })
        );
    }

    deleteReservation(id: string): Observable<void> {
        return this.http.delete<void>(`${this.API2_PATH}/${encodeURIComponent(id)}`);
    }

    cancelReservation(id: string): Observable<{ status: string | null; holdExpiresAt: string | null }> {
        const idStr = String(id || '').trim();
        if (!idStr) throw new Error('Missing reservation id');
        return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(idStr)}/cancel`, {}).pipe(
            map((response) => response?.data || response?.DATA || response || {}),
            map((data: any) => ({
                status: data?.STATUS ?? data?.status ?? null,
                holdExpiresAt: data?.HOLD_EXPIRES_AT ?? data?.hold_expires_at ?? data?.holdExpiresAt ?? null
            }))
        );
    }

    createReservation(payload: { property_id: string; amount?: number | null; payment_plan_id?: string | null; force_pdf?: boolean; fast_reservation_session_id?: string }): Observable<ReservationResult> {
        const key = this.idempotencyKey();
        const headers = new HttpHeaders().set('Idempotency-Key', key);
        const context = new HttpContext().set(SKIP_AUTH_REFRESH, true);
        return this.http
            .post<any>(this.API2_PATH, payload, { headers, context })
            .pipe(map((response) => this.mapCreateResponse(response)));
    }

    reserveOnBehalf(payload: { full_name: string; email: string | null; phone: string; property_id: string; payment_plan_id: string | null }): Observable<{ fastReservationSessionId: string; target: string; targetMasked: string }> {
        const key = this.idempotencyKey();
        const headers = new HttpHeaders().set('Idempotency-Key', key);
        const context = new HttpContext().set(SKIP_AUTH_REFRESH, true);
        return this.http
            .post<any>(`${this.API2_PATH}/on-behalf`, payload, { headers, context })
            .pipe(map((response) => {
                const data = response?.data || response?.DATA || response || {};
                return {
                    fastReservationSessionId: data?.fast_reservation_session_id || data?.fastReservationSessionId || null,
                    target: data?.target || null,
                    targetMasked: data?.target_masked || data?.targetMasked || null
                };
            }));
    }

    activateHold(reservationId: string): Observable<{ status: string | null; holdExpiresAt: string | null; holdExtensionCount: number; holdExtendedAt: string | null }> {
        const id = String(reservationId || '').trim();
        if (!id) throw new Error('Missing reservation id');
        return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/activate-hold`, {}).pipe(
            map((response) => response?.data || response?.DATA || response || {}),
            map((data: any) => ({
                status: data?.STATUS ?? data?.status ?? null,
                holdExpiresAt: data?.HOLD_EXPIRES_AT ?? data?.hold_expires_at ?? data?.holdExpiresAt ?? null,
                holdExtensionCount: Number(data?.HOLD_EXTENSION_COUNT ?? data?.hold_extension_count ?? data?.holdExtensionCount ?? 0) || 0,
                holdExtendedAt: data?.HOLD_EXTENDED_AT ?? data?.hold_extended_at ?? data?.holdExtendedAt ?? null
            }))
        );
    }

    extendHold(reservationId: string): Observable<{ holdExpiresAt: string | null; holdExtensionCount: number; holdExtendedAt: string | null }> {
        const id = String(reservationId || '').trim();
        if (!id) throw new Error('Missing reservation id');
        return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/extend-hold`, {}).pipe(
            map((response) => response?.data || response?.DATA || response || {}),
            map((data: any) => ({
                holdExpiresAt: data?.HOLD_EXPIRES_AT ?? data?.hold_expires_at ?? data?.holdExpiresAt ?? null,
                holdExtensionCount: Number(data?.HOLD_EXTENSION_COUNT ?? data?.hold_extension_count ?? data?.holdExtensionCount ?? 0) || 0,
                holdExtendedAt: data?.HOLD_EXTENDED_AT ?? data?.hold_extended_at ?? data?.holdExtendedAt ?? null
            }))
        );
    }

    cancelHold(reservationId: string): Observable<{ status: string | null; holdExpiresAt: string | null; holdExtensionCount: number; holdExtendedAt: string | null }> {
        const id = String(reservationId || '').trim();
        if (!id) throw new Error('Missing reservation id');
        return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/cancel-hold`, {}).pipe(
            map((response) => response?.data || response?.DATA || response || {}),
            map((data: any) => ({
                status: data?.STATUS ?? data?.status ?? null,
                holdExpiresAt: data?.HOLD_EXPIRES_AT ?? data?.hold_expires_at ?? data?.holdExpiresAt ?? null,
                holdExtensionCount: Number(data?.HOLD_EXTENSION_COUNT ?? data?.hold_extension_count ?? data?.holdExtensionCount ?? 0) || 0,
                holdExtendedAt: data?.HOLD_EXTENDED_AT ?? data?.hold_extended_at ?? data?.holdExtendedAt ?? null
            }))
        );
    }

    // ─── Hold extension workflow (migration 163) ─────────────────────────────

    requestHoldExtension(reservationId: string): Observable<HoldExtensionRequest> {
        const id = String(reservationId || '').trim();
        if (!id) throw new Error('Missing reservation id');
        return this.http
            .post<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/hold-extension-requests`, {})
            .pipe(map((response) => this.mapHoldExtensionRequest(response?.data || response?.DATA || response || {})));
    }

    listHoldExtensionRequests(status: 'pending' | 'all' = 'pending'): Observable<HoldExtensionRequest[]> {
        let params = new HttpParams().set('status', String(status));
        return this.http
            .get<any>(`${this.API2_ROOT}/hold-extension-requests`, { params })
            .pipe(map((response) => (Array.isArray(response?.data) ? response.data : []).map((row: any) => this.mapHoldExtensionRequest(row))));
    }

    decideHoldExtension(requestId: string, decision: 'approved' | 'rejected', comment?: string | null): Observable<HoldExtensionRequest> {
        const id = String(requestId || '').trim();
        if (!id) throw new Error('Missing extension request id');
        return this.http
            .post<any>(`${this.API2_ROOT}/hold-extension-requests/${encodeURIComponent(id)}/decision`, {
                decision,
                comment: comment || null
            })
            .pipe(map((response) => this.mapHoldExtensionRequest(response?.data || response?.DATA || response || {})));
    }

    getHoldExtensionHistory(reservationId: string): Observable<HoldExtensionHistory> {
        const id = String(reservationId || '').trim();
        if (!id) throw new Error('Missing reservation id');
        return this.http.get<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/hold-extension-requests`).pipe(
            map((response) => {
                const data = response?.data || response?.DATA || {};
                return {
                    requests: (Array.isArray(data?.requests) ? data.requests : []).map((row: any) => this.mapHoldExtensionRequest(row)),
                    events: (Array.isArray(data?.events) ? data.events : []).map((row: any) => ({
                        id: String(row?.id || ''),
                        requestId: String(row?.request_id || row?.requestId || ''),
                        action: String(row?.action || ''),
                        comment: row?.comment ?? null,
                        actorName: row?.actor_name ?? row?.actorName ?? null,
                        createdAt: row?.created_at ?? row?.createdAt ?? null
                    }))
                };
            })
        );
    }

    private mapHoldExtensionRequest(row: any): HoldExtensionRequest {
        return {
            id: String(row?.id || ''),
            reservationId: String(row?.reservation_id || row?.reservationId || ''),
            propertyId: String(row?.property_id || row?.propertyId || ''),
            projectId: row?.project_id ? String(row.project_id) : null,
            requestedByUserId: row?.requested_by_user_id ? String(row.requested_by_user_id) : null,
            requesterName: row?.requester_name ?? row?.requesterName ?? null,
            requestedAt: row?.requested_at ?? row?.requestedAt ?? null,
            originalHoldExpiry: row?.original_hold_expiry ?? row?.originalHoldExpiry ?? null,
            decisionDeadlineAt: row?.decision_deadline_at ?? row?.decisionDeadlineAt ?? null,
            extensionHours: Number(row?.extension_hours ?? row?.extensionHours ?? 0) || null,
            approverUserIds: Array.isArray(row?.approver_user_ids ?? row?.approverUserIds)
                ? (row?.approver_user_ids ?? row?.approverUserIds).map((id: any) => String(id))
                : [],
            status: String(row?.status || 'pending'),
            decidedByUserId: row?.decided_by_user_id ? String(row.decided_by_user_id) : null,
            decisionComment: row?.decision_comment ?? row?.decisionComment ?? null,
            decidedAt: row?.decided_at ?? row?.decidedAt ?? null,
            newHoldExpiry: row?.new_hold_expiry ?? row?.newHoldExpiry ?? null,
            propertyTitle: row?.property_title ?? row?.propertyTitle ?? null,
            unitNumber: row?.unit_number ?? row?.unitNumber ?? null,
            projectName: row?.project_name ?? row?.projectName ?? null,
            canDecide: Boolean(row?.can_decide ?? row?.canDecide)
        };
    }

    getReservationKyc(reservationId: string): Observable<ReservationKycRecord> {
        const id = String(reservationId || '').trim();
        if (!id) throw new Error('Missing reservation id');
        const context = new HttpContext().set(SKIP_AUTH_REFRESH, true);
        return this.http.get<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/kyc`, { context }).pipe(
            map((response) => response?.data || response?.DATA || response || {}),
            map((data: any) => this.mapApi2ReservationKyc(data))
        );
    }

  setReservationKycIdDocument(reservationId: string, fileId: string): Observable<ReservationKycRecord> {
    const id = String(reservationId || '').trim();
    const f = String(fileId || '').trim();
    if (!id) throw new Error('Missing reservation id');
    if (!f) throw new Error('Missing file id');
    return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/kyc/id-document`, { file_id: f }).pipe(
      map((response) => response?.data || response?.DATA || response || {}),
      map((data: any) => this.mapApi2ReservationKyc(data))
    );
  }

  setReservationKycIdDocumentType(reservationId: string, fileId: string): Observable<ReservationKycRecord> {
    const id = String(reservationId || '').trim();
    const f = String(fileId || '').trim();
    if (!id) throw new Error('Missing reservation id');
    if (!f) throw new Error('Missing file id');
    return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/kyc/id-document-type`, { file_id: f }).pipe(
      map((response) => response?.data || response?.DATA || response || {}),
      map((data: any) => this.mapApi2ReservationKyc(data))
    );
  }

  setReservationKycIdDocumentBack(reservationId: string, fileId: string): Observable<ReservationKycRecord> {
  const id = String(reservationId || '').trim();
  const f = String(fileId || '').trim();
  if (!id) throw new Error('Missing reservation id');
  if (!f) throw new Error('Missing file id');
  return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/kyc/id-document-back`, { file_id: f }).pipe(
  map((response) => response?.data || response?.DATA || response || {}),
  map((data: any) => this.mapApi2ReservationKyc(data))
  );
  }

  updateReservationKyc(reservationId: string, confirmedData: any): Observable<ReservationKycRecord> {
        const id = String(reservationId || '').trim();
        if (!id) throw new Error('Missing reservation id');
        return this.http.put<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/kyc`, { confirmed_data: confirmedData }).pipe(
            map((response) => response?.data || response?.DATA || response || {}),
            map((data: any) => this.mapApi2ReservationKyc(data))
        );
    }

    approveReservationKyc(reservationId: string): Observable<ReservationKycRecord> {
        const id = String(reservationId || '').trim();
        if (!id) throw new Error('Missing reservation id');
        return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/kyc/approve`, {}).pipe(
            map((response) => response?.data || response?.DATA || response || {}),
            map((data: any) => this.mapApi2ReservationKyc(data))
        );
    }

    ensureZohoSign(reservationId: string, options: { force?: boolean; paymentPlanId?: string | null } = {}): Observable<{ pdfFileId: string | null; zohoSign: ReservationResult['zohoSign'] }> {
        const body: any = {};
        if (options.force) body.force = true;
        if (options.paymentPlanId) body.payment_plan_id = String(options.paymentPlanId).trim();
        return this.http.post<any>(`${this.API2_PATH}/${encodeURIComponent(reservationId)}/zoho-sign`, body).pipe(
            map((response) => {
                const record = response?.data ? response : response?.record || response?.result || response || {};
                const data = record?.data || record?.Data || record?.DATA || null;
                const pdfFileId = data?.PDF_FILE_ID || data?.pdfFileId || data?.pdf_file_id || null;
                const zohoRaw = data?.ZOHO_SIGN || data?.zohoSign || data?.zoho_sign || null;
                const zoho = zohoRaw && typeof zohoRaw === 'object'
                    ? {
                        sign_url: (zohoRaw as any).sign_url ?? (zohoRaw as any).SIGN_URL ?? null,
                        request_id: (zohoRaw as any).request_id ?? (zohoRaw as any).REQUEST_ID ?? null,
                        action_id: (zohoRaw as any).action_id ?? (zohoRaw as any).ACTION_ID ?? null,
                        request_status: (zohoRaw as any).request_status ?? (zohoRaw as any).REQUEST_STATUS ?? null,
                        action_status: (zohoRaw as any).action_status ?? (zohoRaw as any).ACTION_STATUS ?? null,
                        is_signed: (zohoRaw as any).is_signed ?? (zohoRaw as any).IS_SIGNED ?? null,
                        signed_at: (zohoRaw as any).signed_at ?? (zohoRaw as any).SIGNED_AT ?? null,
                        checked_at: (zohoRaw as any).checked_at ?? (zohoRaw as any).CHECKED_AT ?? null
                    }
                    : null;
                return {
                    pdfFileId: pdfFileId ? String(pdfFileId) : null,
                    zohoSign: zoho
                };
            })
        );
    }

    getZohoSignStatus(
        reservationId: string,
        options: { refresh?: boolean } = {}
    ): Observable<{ pdfFileId: string | null; zohoSign: ReservationResult['zohoSign'] }> {
        const id = String(reservationId || '').trim();
        let params = new HttpParams();
        if (options.refresh) params = params.set('refresh', '1');
        return this.http.get<any>(`${this.API2_PATH}/${encodeURIComponent(id)}/zoho-sign`, { params }).pipe(
            map((response) => {
                const record = response?.data ? response : response?.record || response?.result || response || {};
                const data = record?.data || record?.Data || record?.DATA || null;
                const pdfFileId = data?.PDF_FILE_ID || data?.pdfFileId || data?.pdf_file_id || null;
                const zohoRaw = data?.ZOHO_SIGN || data?.zohoSign || data?.zoho_sign || null;
                const zoho = zohoRaw && typeof zohoRaw === 'object'
                    ? {
                        sign_url: (zohoRaw as any).sign_url ?? (zohoRaw as any).SIGN_URL ?? null,
                        request_id: (zohoRaw as any).request_id ?? (zohoRaw as any).REQUEST_ID ?? null,
                        action_id: (zohoRaw as any).action_id ?? (zohoRaw as any).ACTION_ID ?? null,
                        request_status: (zohoRaw as any).request_status ?? (zohoRaw as any).REQUEST_STATUS ?? null,
                        action_status: (zohoRaw as any).action_status ?? (zohoRaw as any).ACTION_STATUS ?? null,
                        is_signed: (zohoRaw as any).is_signed ?? (zohoRaw as any).IS_SIGNED ?? null,
                        signed_at: (zohoRaw as any).signed_at ?? (zohoRaw as any).SIGNED_AT ?? null,
                        checked_at: (zohoRaw as any).checked_at ?? (zohoRaw as any).CHECKED_AT ?? null
                    }
                    : null;
                return {
                    pdfFileId: pdfFileId ? String(pdfFileId) : null,
                    zohoSign: zoho
                };
            })
        );
    }

    private mapApi2ReservationKyc(data: any): ReservationKycRecord {
        return {
            userId: data?.user_id ? String(data.user_id) : null,
            email: data?.email ?? null,
            phone: data?.phone ?? null,
            profilePictureFileId: data?.profile_picture_file_id ? String(data.profile_picture_file_id) : null,
            idDocumentFileId: data?.id_document_file_id ? String(data.id_document_file_id) : null,
    idDocumentBackFileId: data?.id_document_back_file_id ? String(data.id_document_back_file_id) : null,
            documentKind: (() => {
                const v = data?.document_kind ?? data?.documentKind ?? null;
                return v === 'id_card' || v === 'passport' ? v : null;
            })(),
            documentRequiresBack:
                data?.document_requires_back === undefined && data?.documentRequiresBack === undefined
                    ? false
                    : Boolean(data?.document_requires_back ?? data?.documentRequiresBack),
            identityStatus: data?.identity_status ?? null,
            confirmedAt: data?.confirmed_at ?? null,
            verifiedAt: data?.verified_at ?? null,
            confirmedData: data?.confirmed_data ?? null,
            extractedNormalized: data?.extracted_normalized ?? null,
            aiComment: data?.ai_comment ?? null,
            aiReviewRequired: Boolean(data?.ai_review_required),
            userEditedFields: Boolean(data?.user_edited_fields),
            missingRequiredFields: Array.isArray(data?.missing_required_fields ?? data?.missingRequiredFields)
                ? (data?.missing_required_fields ?? data?.missingRequiredFields)
                : [],
            readyForApproval: Boolean(data?.ready_for_approval ?? data?.readyForApproval)
        };
    }

    private mapApi2Reservation(row: any): ReservationRecord {
        const currency = row?.currency || {};
        const zohoRaw = row?.zohoSign ?? row?.zoho_sign ?? row?.ZOHO_SIGN ?? null;
        const zoho =
            zohoRaw && typeof zohoRaw === 'object'
                ? {
                    sign_url: (zohoRaw as any).sign_url ?? (zohoRaw as any).SIGN_URL ?? null,
                    request_id: (zohoRaw as any).request_id ?? (zohoRaw as any).REQUEST_ID ?? null,
                    action_id: (zohoRaw as any).action_id ?? (zohoRaw as any).ACTION_ID ?? null,
                    request_status: (zohoRaw as any).request_status ?? (zohoRaw as any).REQUEST_STATUS ?? null,
                    action_status: (zohoRaw as any).action_status ?? (zohoRaw as any).ACTION_STATUS ?? null,
                    is_signed: (zohoRaw as any).is_signed ?? (zohoRaw as any).IS_SIGNED ?? null,
                    signed_at: (zohoRaw as any).signed_at ?? (zohoRaw as any).SIGNED_AT ?? null,
                    checked_at: (zohoRaw as any).checked_at ?? (zohoRaw as any).CHECKED_AT ?? null
                }
                : null;
        return {
            id: String(row?.id || row?.ROWID || row?.row_id || ''),
            userId: row?.userId ?? row?.user_id ?? row?.USER_ID ?? null,
            propertyLabel: row?.propertyLabel || row?.property_label || row?.property || '—',
            quotaNumber: row?.quotaNumber ?? row?.quota_number ?? null,
            customer: row?.customerName || row?.customer || '—',
            email: row?.customerEmail ?? row?.email ?? null,
            phone: row?.customerPhone ?? row?.phone ?? null,
            amount: typeof row?.amount === 'number' ? row.amount : row?.amount ? Number(row.amount) : null,
            installmentAmount: typeof row?.installmentAmount === 'number' ? row.installmentAmount : null,
            status: row?.status || 'pending',
            reservationFormUrl: row?.reservationFormUrl ?? row?.reservation_form_url ?? null,
            pdfFileId: row?.pdfFileId ?? row?.pdf_file_id ?? null,
            paymentPlanId: (() => {
                const v = row?.paymentPlanId ?? row?.payment_plan_id ?? row?.PAYMENT_PLAN_ID ?? null;
                return v ? String(v) : null;
            })(),
            zohoSign: zoho,
            propertyId: row?.propertyId ?? row?.property_id ?? null,
            createdTime: row?.createdTime ?? row?.created_time ?? null,
            createdByUserId: row?.createdByUserId ?? row?.created_by_user_id ?? row?.CREATED_BY_USER_ID ?? null,
            createdByName: row?.createdByName ?? row?.created_by_name ?? row?.CREATED_BY_NAME ?? null,
            createdByEmail: row?.createdByEmail ?? row?.created_by_email ?? row?.CREATED_BY_EMAIL ?? null,
            createdByPhone: row?.createdByPhone ?? row?.created_by_phone ?? row?.CREATED_BY_PHONE ?? null,
            createdByRole: row?.createdByRole ?? row?.created_by_role ?? row?.CREATED_BY_ROLE ?? null,
            sourceLabel: row?.sourceLabel ?? row?.source_label ?? row?.SOURCE_LABEL ?? null,
            holdExpiresAt: row?.holdExpiresAt ?? row?.HOLD_EXPIRES_AT ?? row?.hold_expires_at ?? null,
            holdExtensionCount: Number(row?.holdExtensionCount ?? row?.HOLD_EXTENSION_COUNT ?? row?.hold_extension_count ?? 0) || 0,
            holdExtendedAt: row?.holdExtendedAt ?? row?.HOLD_EXTENDED_AT ?? row?.hold_extended_at ?? null,
            holdWorkflowEnabled: (() => {
                const v = row?.holdWorkflowEnabled ?? row?.HOLD_WORKFLOW_ENABLED;
                return v === undefined || v === null ? null : Boolean(v);
            })(),
            holdExtensionPending: (() => {
                const v = row?.holdExtensionPending ?? row?.HOLD_EXTENSION_PENDING;
                return v === undefined || v === null ? null : Boolean(v);
            })(),
            holdExtensionHours: (() => {
                const v = row?.holdExtensionHours ?? row?.HOLD_EXTENSION_HOURS;
                const n = v === undefined || v === null ? NaN : Number(v);
                return Number.isFinite(n) && n > 0 ? n : null;
            })(),
            offerRequestLastAttemptAt:
                row?.offerRequestLastAttemptAt ?? row?.OFFER_REQUEST_LAST_ATTEMPT_AT ?? row?.offer_request_last_attempt_at ?? null,
            offerRequestLastMissing:
                row?.offerRequestLastMissing ?? row?.OFFER_REQUEST_LAST_MISSING ?? row?.offer_request_last_missing ?? null,
            offerRequestLastPaymentPlanId: (() => {
                const v =
                    row?.offerRequestLastPaymentPlanId ??
                    row?.OFFER_REQUEST_LAST_PAYMENT_PLAN_ID ??
                    row?.offer_request_last_payment_plan_id ??
                    null;
                return v ? String(v) : null;
            })(),
            offerReady:
                row?.offerReady === undefined && row?.offer_ready === undefined && row?.OFFER_READY === undefined
                    ? null
                    : Boolean(row?.offerReady ?? row?.offer_ready ?? row?.OFFER_READY),
            offerReadyForReservation:
                row?.offerReadyForReservation === undefined &&
                row?.offer_ready_for_reservation === undefined &&
                row?.OFFER_READY_FOR_RESERVATION === undefined
                    ? null
                    : Boolean(row?.offerReadyForReservation ?? row?.offer_ready_for_reservation ?? row?.OFFER_READY_FOR_RESERVATION),
            offerMissingItems: Array.isArray(row?.offerMissingItems ?? row?.offer_missing_items ?? row?.OFFER_MISSING_ITEMS)
                ? (row?.offerMissingItems ?? row?.offer_missing_items ?? row?.OFFER_MISSING_ITEMS).map((item: any) => ({
                    key: String(item?.key || '').trim(),
                    label: String(item?.label || item?.key || '').trim(),
                    ok: item?.ok === undefined ? undefined : Boolean(item.ok),
                    details: item?.details ?? null,
                    fix_path: item?.fix_path ?? null,
                    fix_link: item?.fix_link ?? item?.fix_path ?? null
                })).filter((item: any) => Boolean(item.key && item.label))
                : [],
            currency: {
                code: currency.code ?? currency.CODE ?? null,
                symbol: currency.symbol ?? currency.SYMBOL ?? null
            }
        };
    }

    private mapCreateResponse(response: any): ReservationResult {
        const record = response?.data ? response : response?.record || response?.result || response || {};
        const data = record?.data || record?.Data || record?.DATA || null;
        const rowId = record?.row_id || record?.rowId || data?.ROWID || null;
        const pdfFileId = data?.PDF_FILE_ID || data?.pdfFileId || data?.pdf_file_id || null;
        const zohoRaw = data?.ZOHO_SIGN || data?.zohoSign || data?.zoho_sign || null;
        const zoho = zohoRaw && typeof zohoRaw === 'object'
            ? {
                sign_url: (zohoRaw as any).sign_url ?? (zohoRaw as any).SIGN_URL ?? null,
                request_id: (zohoRaw as any).request_id ?? (zohoRaw as any).REQUEST_ID ?? null,
                action_id: (zohoRaw as any).action_id ?? (zohoRaw as any).ACTION_ID ?? null,
                request_status: (zohoRaw as any).request_status ?? (zohoRaw as any).REQUEST_STATUS ?? null,
                action_status: (zohoRaw as any).action_status ?? (zohoRaw as any).ACTION_STATUS ?? null,
                is_signed: (zohoRaw as any).is_signed ?? (zohoRaw as any).IS_SIGNED ?? null,
                signed_at: (zohoRaw as any).signed_at ?? (zohoRaw as any).SIGNED_AT ?? null,
                checked_at: (zohoRaw as any).checked_at ?? (zohoRaw as any).CHECKED_AT ?? null
            }
            : null;
        return {
            reservationId: rowId ? String(rowId) : null,
            leadId: data?.LEAD_ID ?? data?.lead_id ?? data?.leadId ?? null,
            reservationPdfUrl: null,
            pdfFileId: pdfFileId ? String(pdfFileId) : null,
            paymentPlanId: (() => {
                const v = data?.PAYMENT_PLAN_ID ?? data?.paymentPlanId ?? data?.payment_plan_id ?? null;
                return v ? String(v) : null;
            })(),
            zohoSign: zoho,
            holdExpiresAt: data?.HOLD_EXPIRES_AT ?? data?.hold_expires_at ?? data?.holdExpiresAt ?? null,
            holdExtensionCount: Number(data?.HOLD_EXTENSION_COUNT ?? data?.hold_extension_count ?? data?.holdExtensionCount ?? 0) || 0,
            holdExtendedAt: data?.HOLD_EXTENDED_AT ?? data?.hold_extended_at ?? data?.holdExtendedAt ?? null
        };
    }

    private idempotencyKey(): string {
        const c: any = (globalThis as any)?.crypto;
        if (c?.randomUUID) return String(c.randomUUID());

        if (c?.getRandomValues) {
            const bytes = new Uint8Array(16);
            c.getRandomValues(bytes);
            // RFC 4122 v4
            bytes[6] = (bytes[6] & 0x0f) | 0x40;
            bytes[8] = (bytes[8] & 0x3f) | 0x80;
            const hex = Array.from(bytes).map((b) => b.toString(16).padStart(2, '0'));
            return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex
                .slice(8, 10)
                .join('')}-${hex.slice(10, 16).join('')}`;
        }

        const fallback = () => Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
        return `${fallback()}${fallback()}-${fallback()}-${fallback()}-${fallback()}-${fallback()}${fallback()}${fallback()}`;
    }
}
